/**
 * FintsSetupPage (Phase 4, §7.3) — FinTS/HBCI onboarding for Hamburger
 * Sparkasse (decoupled S-pushTAN), route `bankverbindungen/einrichten/fints`.
 *
 * Three views, driven by local state (no wizard step machinery needed — FinTS
 * setup is a single form):
 *  1. Credentials form — bank preset (Haspa default) or the free-form escape
 *     hatch (BLZ/Server-URL/Bank-Label), `login_id`, PIN. The PIN is cleared
 *     from component state immediately on a successful submit (the
 *     `StepCredential` precedent in `SetupWizard.tsx`) — it must never linger
 *     in memory longer than the request.
 *  2. `TanWaitPanel` while a decoupled-TAN challenge is outstanding (either
 *     from setup or from the history-bootstrap sync fired from view 3).
 *  3. The discovered accounts (`AccountMappingRow` reuse) plus a
 *     "Verlauf importieren (bis zu 2 Jahre)" button that fires a `bootstrap`
 *     FinTS sync — itself usually another TAN challenge, handled by the same
 *     `TanWaitPanel`.
 *
 * Hidden entirely (an inline notice, no form) when the FinTS channel is not
 * configured server-side (`useFintsAvailability().data?.enabled === false`) —
 * mirrors the `SetupWizard` entry-card gating.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, History } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { SegmentedControl } from '../../components/ui/SegmentedControl.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { useFintsAvailability, useFintsConnect, useFintsSync } from '../../lib/hooks/useFints.ts';
import type { ConnectionAccountOut, FintsBankPreset, FintsChallengeOut } from '../../lib/types.ts';
import { AccountMappingRow } from './components/AccountMappingRow.tsx';
import { TanWaitPanel } from './components/TanWaitPanel.tsx';

const CUSTOM_BANK = 'custom';

export default function FintsSetupPage() {
  const navigate = useNavigate();
  const availability = useFintsAvailability();

  return (
    <div>
      <PageHeader
        title="FinTS verbinden"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void navigate('/bankverbindungen/einrichten')}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Zurück</span>
          </Button>
        }
      />

      <div className="w-full space-y-6 px-4 py-6 sm:px-6">
        {availability.isPending ? (
          <SkeletonList rows={3} />
        ) : availability.isError ? (
          <ErrorState error={availability.error} onRetry={() => void availability.refetch()} />
        ) : !availability.data.enabled ? (
          <ErrorState
            title="FinTS ist noch nicht eingerichtet"
            message="Der FinTS-Kanal ist derzeit nicht verfügbar. Bitte nutze Enable Banking oder wende dich an den Administrator."
          />
        ) : (
          <FintsSetupFlow banks={availability.data.banks} />
        )}
      </div>
    </div>
  );
}

function FintsSetupFlow({ banks }: { banks: FintsBankPreset[] }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const connect = useFintsConnect();
  const fintsSync = useFintsSync();

  // `null` until the user picks explicitly — defaults to the first preset
  // (Haspa) without needing an effect to sync it into state.
  const [bankKey, setBankKey] = useState<string | null>(null);
  const effectiveBankKey = bankKey ?? banks[0]?.key ?? CUSTOM_BANK;
  const [blz, setBlz] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [bankLabel, setBankLabel] = useState('');
  const [loginId, setLoginId] = useState('');
  const [pin, setPin] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [connectionId, setConnectionId] = useState<number | null>(null);
  const [setupChallenge, setSetupChallenge] = useState<FintsChallengeOut | null>(null);
  const [accounts, setAccounts] = useState<ConnectionAccountOut[] | null>(null);
  const [bootstrapChallenge, setBootstrapChallenge] = useState<FintsChallengeOut | null>(null);

  function submit() {
    if (!loginId.trim()) {
      setFormError('Bitte den FinTS-Login eingeben.');
      return;
    }
    if (!pin.trim()) {
      setFormError('Bitte die PIN eingeben.');
      return;
    }
    if (effectiveBankKey === CUSTOM_BANK) {
      if (!blz.trim() || !serverUrl.trim() || !bankLabel.trim()) {
        setFormError('Bitte BLZ, Server-URL und Bankname vollständig ausfüllen.');
        return;
      }
      if (!serverUrl.trim().startsWith('https://')) {
        setFormError('Die Server-URL muss mit https:// beginnen.');
        return;
      }
    }
    setFormError(null);

    const body =
      effectiveBankKey === CUSTOM_BANK
        ? {
            blz: blz.trim(),
            server_url: serverUrl.trim(),
            bank_label: bankLabel.trim(),
            login_id: loginId.trim(),
            pin,
          }
        : { bank: effectiveBankKey, login_id: loginId.trim(), pin };

    connect.mutate(body, {
      onSuccess: (data) => {
        // Clear the PIN from component state immediately after submit.
        setPin('');
        setConnectionId(data.connection_id);
        if (data.challenge) {
          setSetupChallenge(data.challenge);
        } else {
          setAccounts(data.accounts ?? []);
        }
      },
      onError: (err) => {
        setPin('');
        setFormError(err.message);
      },
    });
  }

  function runBootstrap() {
    if (connectionId == null) return;
    fintsSync.mutate(
      { id: connectionId, body: { trigger: 'bootstrap' } },
      {
        onSuccess: (data) => {
          if (data.challenge) {
            setBootstrapChallenge(data.challenge);
          } else {
            toast('Verlauf importiert.', { variant: 'success' });
          }
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  if (setupChallenge) {
    return (
      <TanWaitPanel
        challengeId={setupChallenge.id}
        onCompleted={(result) => {
          setSetupChallenge(null);
          setAccounts(result.accounts ?? []);
        }}
        onRetry={() => setSetupChallenge(null)}
      />
    );
  }

  if (accounts) {
    return (
      <div className="space-y-5">
        <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Gefundene Konten
        </p>
        {accounts.length > 0 ? (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {accounts.map((account) => (
              <AccountMappingRow key={account.account_id} account={account} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400 dark:text-slate-500">Keine Konten gefunden.</p>
        )}

        {bootstrapChallenge ? (
          <TanWaitPanel
            challengeId={bootstrapChallenge.id}
            onCompleted={() => {
              setBootstrapChallenge(null);
              toast('Verlauf importiert.', { variant: 'success' });
            }}
            onRetry={() => setBootstrapChallenge(null)}
          />
        ) : (
          <div className="flex flex-wrap justify-between gap-2 border-t border-slate-100 pt-4 dark:border-slate-800/70">
            <Button
              variant="secondary"
              onClick={runBootstrap}
              disabled={fintsSync.isPending || connectionId == null}
            >
              <History className="h-4 w-4" />
              {fintsSync.isPending ? 'Wird gestartet …' : 'Verlauf importieren (bis zu 2 Jahre)'}
            </Button>
            <Button
              onClick={() => void navigate(`/bankverbindungen/${connectionId ?? ''}`)}
              disabled={connectionId == null}
            >
              Fertig
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  const bankOptions = [
    ...banks.map((b) => ({ value: b.key, label: b.label })),
    { value: CUSTOM_BANK, label: 'Andere Bank (erweitert)' },
  ];

  return (
    <div className="space-y-5">
      <Field label="Bank">
        {() => (
          <SegmentedControl
            value={effectiveBankKey}
            onChange={setBankKey}
            ariaLabel="Bank"
            options={bankOptions}
          />
        )}
      </Field>

      {effectiveBankKey === CUSTOM_BANK ? (
        <>
          <Field label="Bankname">
            {(id) => (
              <Input id={id} value={bankLabel} onChange={(e) => setBankLabel(e.target.value)} />
            )}
          </Field>
          <Field label="Bankleitzahl (BLZ)">
            {(id) => (
              <Input
                id={id}
                value={blz}
                onChange={(e) => setBlz(e.target.value)}
                inputMode="numeric"
                placeholder="z. B. 20050550"
              />
            )}
          </Field>
          <Field label="FinTS-Server-URL" hint="Muss mit https:// beginnen.">
            {(id) => (
              <Input
                id={id}
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="https://…"
              />
            )}
          </Field>
        </>
      ) : null}

      <Field label="FinTS-Login (Anmeldename)" required>
        {(id) => (
          <Input
            id={id}
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            autoComplete="off"
          />
        )}
      </Field>

      <Field label="PIN" hint="Wird verschlüsselt gespeichert." required>
        {(id) => (
          <Input
            id={id}
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoComplete="off"
          />
        )}
      </Field>

      {formError ? <p className="text-sm text-rose-600 dark:text-rose-400">{formError}</p> : null}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={connect.isPending}>
          {connect.isPending ? 'Verbinde …' : 'Verbinden'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
